import Layout from '@/components/Layout';
import { motion } from 'framer-motion';
import LegalSEO from '@/components/LegalSEO';
import { LEGAL_SEO } from '@/lib/legalSeo';
import { FileText, MapPin, Users, AlertTriangle, Phone, Ship, Anchor, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const companyInfo = [
  { label: 'Bedrijf', value: "L'Orange CV" },
  { label: 'Eigenaar', value: 'Laurens Sinaasappel' },
  { label: 'Adres', value: 'Klaproos 30, 1422 KL Uithoorn' },
  { label: 'Telefoon', value: '+31(0) 6 215 06 336' },
  { label: 'E-mail', value: 'info@johannaengelina.nl' },
  { label: 'KvK', value: '33268676' },
];

const sections = [
  {
    icon: Ship,
    title: 'Boekingen',
    content: 'Bij L\'Orange CV kunt u de Johanna Engelina voor u en uw gezelschap boeken vanaf minimaal 10 personen. Is uw gezelschap kleiner dan tien personen, dan kunt u gebruik maken van ons individuele programma.',
  },
  {
    icon: MapPin,
    title: 'Afvaarthaven',
    content: 'In het hoogseizoen heeft de Johanna Engelina Harlingen als vaste vertrekhaven. Buiten het seizoen is dat Amsterdam. Een andere vertrekhaven is mogelijk tegen extra aanvaarkosten.',
  },
  {
    icon: Users,
    title: 'Maximum aantal personen',
    content: 'Het maximaal toegestane aantal passagiers is bepaald door de Scheepvaartinspectie. Bij meerdaagse tochten met catering: max 31 i.p.v. 33 gasten (ruimte voor cateringpersoneel).',
  },
  {
    icon: AlertTriangle,
    title: 'Annuleringsvoorwaarden',
    items: [
      { label: 'Groepen — tot 90 dagen', value: '50% van de huursom' },
      { label: 'Groepen — binnen 90 dagen', value: '100% van de huursom' },
      { label: 'Individueel — tot 30 dagen', value: '50% van het tarief' },
      { label: 'Individueel — binnen 30 dagen', value: '100% van het tarief' },
    ],
  },
];

export default function AGBPage() {
  return (
    <Layout>
      <LegalSEO base="/agb/" />
      {/* Header */}
      <div className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/3 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-32 pb-12 relative">
          <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <FileText size={18} className="text-primary" />
              </div>
              <p className="text-primary font-bold text-xs uppercase tracking-[0.2em]">Juridisch</p>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-foreground mb-3">
              {LEGAL_SEO['/agb/'].h1}
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl">
              {LEGAL_SEO['/agb/'].intro}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          
          {/* Main content — 2 cols */}
          <div className="lg:col-span-2 space-y-6">
            {sections.map((section, i) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={i}
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                  className="bg-card border border-border rounded-sm p-6 md:p-8"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-display text-lg font-bold text-foreground mb-3">{section.title}</h2>
                      {section.content && (
                        <p className="text-foreground/80 leading-relaxed">{section.content}</p>
                      )}
                      {section.items && (
                        <div className="bg-background/50 border border-border rounded-sm overflow-hidden mt-2">
                          {section.items.map((item, j) => (
                            <div
                              key={j}
                              className={`flex justify-between items-center px-4 py-3 text-sm ${
                                j < section.items.length - 1 ? 'border-b border-border' : ''
                              }`}
                            >
                              <span className="text-muted-foreground">{item.label}</span>
                              <span className="text-foreground font-medium">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Sidebar — 1 col */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-6"
          >
            {/* Company card */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Anchor size={14} className="text-primary" />
                <h3 className="font-display text-sm font-bold uppercase tracking-wider text-primary">Bedrijfsgegevens</h3>
              </div>
              <dl className="space-y-3">
                {companyInfo.map((item, i) => (
                  <div key={i}>
                    <dt className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{item.label}</dt>
                    <dd className="text-sm text-foreground font-medium mt-0.5">
                      {item.value.includes('@') ? (
                        <a href={`mailto:${item.value}`} className="hover:text-primary transition-colors">{item.value}</a>
                      ) : item.value.includes('+') ? (
                        <a href={`tel:${item.value.replace(/[\s()]/g, '')}`} className="hover:text-primary transition-colors">{item.value}</a>
                      ) : (
                        item.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Quick links */}
            <div className="bg-primary/5 border border-primary/20 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} className="text-primary" />
                <h3 className="text-sm font-bold text-foreground">Goed om te weten</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                Heeft u vragen over een boeking of onze voorwaarden? Neem gerust contact op.
              </p>
              <div className="space-y-2">
                <Link to="/contact/" className="btn-johanna w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-sm">
                  Contact <ArrowRight size={14} />
                </Link>
                <Link to="/prijzen/" className="btn-johanna-outline w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-sm">
                  Prijzen & tarieven
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}